import React, { useEffect, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";

import { SectionTitle } from "@/components/settings/SettingsPage";
import { colors, radius, text } from "@/components/settings/theme";
import { fetchEventViewerHours } from "@/components/http/viewerHours";
import { t, tn } from "@/localization";
import LiveViewersChart from "./LiveViewersChart";
import {
  COUNTED_FROM,
  atEventTime,
  chartSessions,
  dayRows,
  fairUse,
  formatHours,
  liveSessions,
  startedBeforeCounts,
} from "./logic";

// Event Analytics' viewer-hours (CDN, counted every hour): the usage card the
// videographer sees above the report (not in the PDF), and the live viewers
// chart inside the report.

const DAYS_SHOWN = 5;
const NEAR_LIMIT = 0.8;

// `enabled` false (the event shows Mux's report): nothing to load.
export const useEventViewerHours = (event, enabled = true) => {
  const [state, setState] = useState({ loading: enabled, data: null });
  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, data: null });
      return;
    }
    let current = true;
    setState({ loading: true, data: null });
    fetchEventViewerHours(event).then((data) => {
      if (current) setState({ loading: false, data });
    });
    return () => {
      current = false;
    };
  }, [event?.id, enabled]);
  return state;
};

const hoursLabel = (hours) =>
  tn("{count} viewer-hour", "{count} viewer-hours", hours === 1 ? 1 : 2, {
    count: formatHours(hours),
  });

const Meter = ({ share, color }) => (
  <View
    accessible={false}
    style={{ height: 8, borderRadius: radius.pill, overflow: "hidden" }}
  >
    <View
      style={[
        tw`absolute inset-0`,
        { backgroundColor: color, opacity: 0.15 },
      ]}
    />
    <View
      style={{
        width: `${Math.max(1.5, share * 100)}%`,
        height: "100%",
        borderRadius: radius.pill,
        backgroundColor: color,
      }}
    />
  </View>
);

const Allowance = ({ total }) => {
  if (total.ticketed) {
    return (
      <Text style={[text.hint, tw`mt-3`]}>
        {t("Ticketed events have no viewer-hour limit.")}
      </Text>
    );
  }
  const use = fairUse(total);
  if (!use) return null;
  const near = !use.over && use.share >= NEAR_LIMIT;
  const color = use.over || near ? colors.warning : colors.slate;
  return (
    <View
      style={tw`mt-3`}
      accessible
      accessibilityLabel={t("{used} of {limit} viewer-hours included with this event", {
        used: formatHours(use.used),
        limit: formatHours(use.limit),
      })}
    >
      <Meter share={use.share} color={color} />
      <View style={tw`flex-row items-center mt-2`}>
        {(use.over || near) && (
          <Ionicons
            name="alert-circle"
            size={16}
            color={colors.warning}
            style={tw`mr-1`}
          />
        )}
        <Text style={[text.hint, tw`flex-1`]}>
          {use.over
            ? t(
                "Over the {limit} viewer-hours included with this event by {over}.",
                {
                  limit: formatHours(use.limit),
                  over: formatHours(use.used - use.limit),
                }
              )
            : t("{used} of {limit} viewer-hours included with this event", {
                used: formatHours(use.used),
                limit: formatHours(use.limit),
              })}
        </Text>
      </View>
    </View>
  );
};

const DayRow = ({ row }) => (
  <View
    style={[
      tw`flex-row items-center py-2`,
      { borderTopWidth: 1, borderTopColor: colors.divider },
    ]}
  >
    <Text style={[text.body, tw`flex-1`]}>
      {moment.utc(row.day).format(t("ddd, MMM D"))}
    </Text>
    <Text style={text.hint}>
      {t("Live {live} · Replay {replay}", {
        live: formatHours(row.live),
        replay: formatHours(row.replay),
      })}
    </Text>
  </View>
);

const ByDay = ({ days }) => {
  const [all, setAll] = useState(false);
  const rows = dayRows(days);
  if (rows.length < 2) return null;
  const shown = all ? rows : rows.slice(0, DAYS_SHOWN);
  return (
    <View style={tw`mt-4`}>
      <Text style={[text.label, tw`mb-1`]}>{t("By day (UTC)")}</Text>
      {shown.map((row) => (
        <DayRow key={row.day} row={row} />
      ))}
      {rows.length > DAYS_SHOWN && (
        <Pressable
          accessibilityRole="button"
          onPress={() => setAll(!all)}
          style={tw`pt-2`}
        >
          <Text style={text.link}>
            {all
              ? t("Show fewer days")
              : tn("Show all {count} days", "Show all {count} days", rows.length)}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

// The usage card: the event's viewer-hours and its 5,000 included hours.
export const ViewerHoursUsage = ({ event, state, style }) => {
  if (state.loading) return null;
  // Only Mux's history (counted: false) is nothing counted here.
  const total = state.data && state.data.counted !== false ? state.data : null;
  const started = event?.event_datetime_utc && moment(event.event_datetime_utc).isBefore();
  if (!total) {
    // Nothing counted yet: say when it will be, for events that could have viewers.
    if (!started || event.event_datetime_utc.slice(0, 10) < COUNTED_FROM) return null;
    return (
      <View style={style}>
        <SectionTitle title={t("Viewer-hours")} help="analytics.viewerHours" />
        <Text style={text.hint}>
          {t("Viewer-hours are updated every hour. They show here within an hour of people watching.")}
        </Text>
      </View>
    );
  }
  return (
    <View style={style}>
      <SectionTitle title={t("Viewer-hours")} help="analytics.viewerHours" />
      <View
        style={[
          tw`p-4`,
          {
            borderWidth: 1,
            borderColor: colors.divider,
            borderRadius: radius.card,
            backgroundColor: colors.white,
          },
        ]}
      >
        <Text style={text.pageTitle}>{hoursLabel(total.hours)}</Text>
        <Text style={[text.hint, tw`mt-0.5`]}>
          {t("Live {live} · Replay {replay}", {
            live: formatHours(total.live_hours),
            replay: formatHours(total.replay_hours),
          })}
        </Text>
        <Allowance total={total} />
        <ByDay days={total.days} />
        <Text style={[text.hint, tw`mt-4`]}>
          {startedBeforeCounts(event)
            ? t(
                "Counted every hour from the video our CDN delivered, on every player. Viewing before {date} isn't included. Updated {updated}.",
                {
                  date: moment.utc(COUNTED_FROM).format(t("MMM D, YYYY")),
                  updated: moment(total.updated_at).format(t("MMM D, YYYY")),
                }
              )
            : t(
                "Counted every hour from the video our CDN delivered, on every player. Updated {updated}.",
                { updated: moment(total.updated_at).format(t("MMM D, YYYY")) }
              )}
        </Text>
      </View>
    </View>
  );
};

// Inside the report: live viewers per minute, one chart per live stretch.
export const LiveViewers = ({ event, state, style }) => {
  const sessions = liveSessions(state.data?.days);
  if (!sessions.length) return null;
  const timezone = event?.event_timezone;
  return (
    <View style={style}>
      <SectionTitle
        title={t("Live viewers")}
        description={t("Devices watching each minute of the live stream.")}
      />
      {chartSessions(sessions).map((session) => (
        <View key={session.start} style={tw`mb-4`}>
          {sessions.length > 1 && (
            <Text style={[text.label, tw`mb-1`]}>
              {atEventTime(session.start, timezone).format(t("ddd, MMM D"))}
            </Text>
          )}
          <LiveViewersChart session={session} timezone={timezone} />
        </View>
      ))}
    </View>
  );
};
